import { TZDate, TZDateMini } from '@date-fns/tz';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { TimeDetails } from '../types/time.js';

type ExpressionDetails = { offset: number; unit: string };

// Time input formats that are accepted by the date-picker (in addition to ISO string)
// Missing information is automatically added using 'startOfToday'
const TIME_INPUT_FORMATS = [
  'yyyy-MM-dd',
  'yyyy-MM-dd H:mm',
  'yyyy-MM-dd H:mm:ss',
  'H:mm',
  'H:mm:ss',
];

// for provided iso8601 format, only allow UTC timezone
const UTC_ISO_TIME_EXPRESSION =
  /(?<iso>^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(\.\d{1,3})?)(?<extended_precision>\d*)(?<timezone>Z)$/;

/**
 * Regular expression used to determine whether the given candidate string
 * is a timeframe expression.
 * @public
 */
export const TIMEFRAME_EXPRESSION = /^(?:now)?([+-]?\d+)(\w+)$/;

/** Parses the input for expression details */
const parseTimeExpression = (
  candidate: string,
): ExpressionDetails | undefined => {
  const [expression, offset, unit] =
    candidate.match(TIMEFRAME_EXPRESSION) ?? [];

  if (expression) {
    return { offset: parseInt(offset), unit };
  }
};

/** Converts expression details into a date object */
const convertExpressionToDate = (
  { offset, unit }: ExpressionDetails,
  relativeDate: number,
): Date | null => {
  const now = new TZDateMini(relativeDate, getTimezone());
  switch (unit) {
    case 's':
      return addSeconds(now, offset);
    case 'm':
      return addMinutes(now, offset);
    case 'h':
      return addHours(now, offset);
    case 'd':
      return addDays(now, offset);
    case 'w':
      return addWeeks(now, offset);
    case 'M':
      return addMonths(now, offset);
    case 'y':
      return addYears(now, offset);
  }

  return null;
};

/** Generates a date object out of the input string if provided in one of the accepted formats */
const parseDatetimeInput = (candidate: string): TZDate | null => {
  const referenceDate = startOfDay(TZDateMini.tz(getTimezone()));

  for (const format of TIME_INPUT_FORMATS) {
    const date = parse(candidate, format, referenceDate);
    if (isValid(date)) {
      return date;
    }
  }

  return null;
};

/**
 * Checks if the provided value is a valid ISO string with support for extended (up tp nano) precision.
 * We cannot use dateFns parseISO because it returns native Date object, which do not support extended precision.
 */
const parseISOWithExtendedPrecision = (
  candidate: string,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
): TimeDetails | null => {
  const match = candidate.match(UTC_ISO_TIME_EXPRESSION);
  if (!match?.groups) {
    return null;
  }

  const iso = match.groups['iso'];
  /* contains all precision digits smaller than milli seconds */
  const extended_precision = match.groups['extended_precision'] ?? '';
  const timezone = match.groups['timezone'];

  return {
    type: 'iso8601',
    normalized: `${iso}${extended_precision}${timezone}`,
    date: new TZDateMini(candidate, getTimezone()), // note: date can't handle precision smaller than ms
  };
};

/**
 * You can use the `parseTime` utility function to convert a string into
 * `TimeDetails`, giving you a normalized version of the input string (trimmed
 * string), a `Date` object created from the given input, and the type of input.
 * The type can be either an `expression` or `iso8601` string. If it is not
 * possible to convert the input, the function will return `null`.
 *
 * @deprecated - Please use the `parseTimeAsTimeValue` from `@dynatrace-sdk/units` instead.
 * @public
 */
export const parseTime = (
  candidate?: string | null,
  relativeDate = TZDateMini.tz(getTimezone()).getTime(),
  // eslint-disable-next-line @typescript-eslint/no-deprecated
): TimeDetails | null => {
  if (!candidate) {
    return null;
  }
  const configuredTimezone = getTimezone();
  const normalized = candidate.trim();

  // Check for 'now' expression
  if (normalized === 'now') {
    return {
      type: 'expression',
      normalized,
      date: new TZDateMini(relativeDate, configuredTimezone),
    };
  }

  // Check for time expression format
  const timeDetails = parseTimeExpression(normalized);
  if (timeDetails) {
    const date = convertExpressionToDate(timeDetails, relativeDate);
    const { offset, unit } = { ...timeDetails };

    return date
      ? {
          type: 'expression',
          normalized: `now${offset < 0 ? offset : `+${offset}`}${unit}`,
          date,
        }
      : null;
  }

  // Check if input was already provided as ISO string
  const parsedISODateDetails = parseISOWithExtendedPrecision(normalized);

  if (parsedISODateDetails) {
    return parsedISODateDetails;
  }

  // Check for supported date format
  const date = parseDatetimeInput(normalized);

  if (date) {
    return {
      type: 'iso8601',
      normalized: date.toISOString(),
      date: date,
    };
  }

  return null;
};
